import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { getStoredLanguage, setStoredLanguage } from '../lib/languages';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [activeOrganization, setActiveOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preferredLanguage, setPreferredLanguageState] = useState(getStoredLanguage() || 'en');

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const userData = await api.get('/auth/me');
        setUser(userData);

        // Fetch org to get branding
        if (userData.organization_id) {
          try {
            const orgs = await api.get('/organizations');
            if (orgs && orgs.length > 0) {
              setActiveOrganization(orgs[0]);
            }
          } catch (e) {
            console.error("Failed to fetch organization for branding", e);
          }
        }

        // Sync language preference from server if available
        if (userData.preferred_language) {
          setPreferredLanguageState(userData.preferred_language);
          setStoredLanguage(userData.preferred_language);
        }
      } catch (error) {
        localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    localStorage.setItem('token', response.token);
    setUser(response.user);

    if (response.user?.organization_id) {
      try {
        const orgs = await api.get('/organizations');
        if (orgs && orgs.length > 0) setActiveOrganization(orgs[0]);
      } catch (e) { }
    }

    // Sync language preference from server
    if (response.user?.preferred_language) {
      setPreferredLanguageState(response.user.preferred_language);
      setStoredLanguage(response.user.preferred_language);
    }
    return response;
  };

  const signup = async (data) => {
    // Include language preference in signup
    const storedLang = getStoredLanguage();
    const signupData = {
      ...data,
      preferred_language: storedLang || 'en'
    };
    const response = await api.post('/auth/signup', signupData);
    localStorage.setItem('token', response.token);
    setUser(response.user);
    return response;
  };

  const queryClient = useQueryClient();

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    setUser(null);
    setActiveOrganization(null);
    queryClient.clear(); // Clear all cached data

    // Clear custom branding variables
    const root = document.documentElement;
    root.style.removeProperty('--primary');
    root.style.removeProperty('--ring');
    root.style.removeProperty('--accent');
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  const updateActiveOrganization = (orgData) => {
    setActiveOrganization(prev => ({ ...prev, ...orgData }));
  };

  // Update language preference (both locally and on server)
  const setPreferredLanguage = async (langCode) => {
    setPreferredLanguageState(langCode);
    setStoredLanguage(langCode);

    // If authenticated, update on server
    if (user) {
      try {
        await api.put('/auth/me', { preferred_language: langCode });
        setUser(prev => ({ ...prev, preferred_language: langCode }));
      } catch (error) {
        console.error('Failed to update language preference on server:', error);
      }
    }
  };

  // Check if user needs to choose language (no preference set)
  const needsLanguageSelection = () => {
    // If authenticated and no preference on server
    if (user && !user.preferred_language) {
      return true;
    }
    // If not authenticated and no local storage preference
    if (!user && !getStoredLanguage()) {
      return true;
    }
    return false;
  };

  return (
    <AuthContext.Provider value={{
      user,
      activeOrganization,
      loading,
      login,
      signup,
      logout,
      updateUser,
      updateActiveOrganization,
      checkAuth,
      preferredLanguage,
      setPreferredLanguage,
      needsLanguageSelection
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
